// The chart-of-accounts mapping (docs/plans/phase3-accounting-and-revenue-share.md §1.1, §3).
//
// Wodi posts to a fixed list of account keys. Each key has a default name, so a journal can
// be produced before anyone maps anything; the organisation overrides any key with its own
// account code and name, and can map the payer receivable per payer (SHA, each insurer).
// The keys are a contract: never rename one, because closed periods store them.

import type { PaymentMethod, RefundMethod } from '../billing/shift.ts';
import { REFUND_METHOD_SOURCE } from '../billing/shift.ts';
import type { ServiceItemCategory } from '../catalogue.ts';
import type { RemittanceMethod } from '../insurance/remittance.ts';

export type AccountGroup =
  'revenue' | 'contra_revenue' | 'asset' | 'liability' | 'expense' | 'equity';

type RevenueKey = `revenue.${ServiceItemCategory}`;

export const ACCOUNT_KEYS = [
  // One revenue key per service category, written out so the list is a fixed tuple (the
  // database enum copies it); a test checks every category has one.
  'revenue.consultation',
  'revenue.procedure',
  'revenue.lab_test',
  'revenue.imaging',
  'revenue.medicine',
  'revenue.consumable',
  'revenue.bed_day',
  'revenue.nursing',
  'revenue.mortuary',
  'revenue.other',
  'contra.waivers',
  'contra.discounts',
  'contra.credit_notes',
  'receivable.patients',
  'receivable.payer',
  'money.cash',
  'money.mpesa',
  'money.card',
  'money.bank',
  'cash.over_short',
  'payer.write_offs',
  'inventory',
  'cost_of_sales',
  'stock.losses_gains',
  'opening_balances',
  'goods_received_not_invoiced',
  'payables.suppliers',
  'vat.input',
  'purchases.rounding',
  'doctor_share.expense',
  'doctor_share.payable',
] as const;

export type AccountKey = (typeof ACCOUNT_KEYS)[number];

const KEY_SET: ReadonlySet<string> = new Set(ACCOUNT_KEYS);

export function isAccountKey(value: string): value is AccountKey {
  return KEY_SET.has(value);
}

/** Keys that can be mapped per payer as well as once for all payers. */
export const PAYER_SCOPED_KEYS: readonly AccountKey[] = ['receivable.payer'];

const CATEGORY_WORDS: Record<ServiceItemCategory, string> = {
  consultation: 'Consultations',
  procedure: 'Procedures',
  lab_test: 'Laboratory',
  imaging: 'Imaging',
  medicine: 'Medicines',
  consumable: 'Consumables',
  bed_day: 'Bed days',
  nursing: 'Nursing',
  mortuary: 'Mortuary',
  other: 'Other services',
};

interface AccountDefault {
  readonly group: AccountGroup;
  readonly name: string;
  /** What posts here, in a sentence for the mapping screen. */
  readonly description: string;
}

const FIXED_DEFAULTS: Record<Exclude<AccountKey, RevenueKey>, AccountDefault> = {
  'contra.waivers': {
    group: 'contra_revenue',
    name: 'Waivers',
    description: 'Approved waivers of what patients owe.',
  },
  'contra.discounts': {
    group: 'contra_revenue',
    name: 'Discounts',
    description: 'Approved discounts on what patients owe.',
  },
  'contra.credit_notes': {
    group: 'contra_revenue',
    name: 'Credit notes',
    description: 'Approved credit notes against patient invoices.',
  },
  'receivable.patients': {
    group: 'asset',
    name: 'Patient receivables',
    description:
      "Patients' shares of charges, less payments, waivers and credit notes. Not an accounts receivable account in QuickBooks: lines carry no patient name.",
  },
  'receivable.payer': {
    group: 'asset',
    name: 'Payer receivables',
    description: "SHA's and insurers' shares of charges, less settlements and write-offs.",
  },
  'money.cash': { group: 'asset', name: 'Cash', description: 'Cash payments and refunds.' },
  'money.mpesa': {
    group: 'asset',
    name: 'M-Pesa',
    description: 'M-Pesa payments, paybill payments, reversals and M-Pesa remittances.',
  },
  'money.card': { group: 'asset', name: 'Card clearing', description: 'Card payments.' },
  'money.bank': {
    group: 'asset',
    name: 'Bank',
    description: 'Bank transfers, insurer and SHA settlements, and bank payouts.',
  },
  'cash.over_short': {
    group: 'expense',
    name: 'Cash over and short',
    description: 'Cashier shift variances at close.',
  },
  'payer.write_offs': {
    group: 'expense',
    name: 'Payer write-offs',
    description: 'Approved write-offs of SHA and insurer claims.',
  },
  inventory: {
    group: 'asset',
    name: 'Inventory',
    description: 'Stock at cost: goods received, issued, adjusted and counted.',
  },
  cost_of_sales: {
    group: 'expense',
    name: 'Cost of sales',
    description: 'Stock dispensed, sold, given on the ward or as vaccines, less returns.',
  },
  'stock.losses_gains': {
    group: 'expense',
    name: 'Stock losses and gains',
    description: 'Approved stock adjustments and count variances.',
  },
  opening_balances: {
    group: 'equity',
    name: 'Opening balances',
    description: 'Opening stock balances entered in Wodi.',
  },
  goods_received_not_invoiced: {
    group: 'liability',
    name: 'Goods received not invoiced',
    description: 'Goods received, cleared when the supplier invoice is approved.',
  },
  'payables.suppliers': {
    group: 'liability',
    name: 'Supplier payables',
    description: 'Approved supplier invoices, by supplier.',
  },
  'vat.input': {
    group: 'asset',
    name: 'Input VAT',
    description:
      'VAT printed on approved supplier invoices. Map it to a cost account if the facility cannot reclaim VAT.',
  },
  'purchases.rounding': {
    group: 'expense',
    name: 'Purchase rounding',
    description:
      "The difference between a supplier invoice's printed total and its lines plus VAT.",
  },
  'doctor_share.expense': {
    group: 'expense',
    name: 'Doctor revenue share',
    description: 'Approved revenue share statements.',
  },
  'doctor_share.payable': {
    group: 'liability',
    name: 'Doctor revenue share payable',
    description: 'Approved revenue share statements, less payouts recorded.',
  },
};

export function accountDefault(key: AccountKey): AccountDefault {
  if (key.startsWith('revenue.')) {
    const category = key.slice('revenue.'.length) as ServiceItemCategory;
    return {
      group: 'revenue',
      name: `Revenue: ${CATEGORY_WORDS[category]}`,
      description: `Charges in the ${CATEGORY_WORDS[category].toLowerCase()} category.`,
    };
  }
  return FIXED_DEFAULTS[key as Exclude<AccountKey, RevenueKey>];
}

export function revenueAccountOf(category: ServiceItemCategory): AccountKey {
  return `revenue.${category}`;
}

const PAYMENT_ACCOUNTS: Record<PaymentMethod, AccountKey> = {
  cash: 'money.cash',
  mpesa: 'money.mpesa',
  card: 'money.card',
  bank_transfer: 'money.bank',
};

export function paymentAccountOf(method: PaymentMethod): AccountKey {
  return PAYMENT_ACCOUNTS[method];
}

/** A refund leaves by the method it reverses: an M-Pesa reversal from M-Pesa. */
export function refundAccountOf(method: RefundMethod): AccountKey {
  return PAYMENT_ACCOUNTS[REFUND_METHOD_SOURCE[method]];
}

const REMITTANCE_ACCOUNTS: Record<RemittanceMethod, AccountKey> = {
  eft: 'money.bank',
  cheque: 'money.bank',
  mpesa: 'money.mpesa',
  other: 'money.bank',
};

export function remittanceAccountOf(method: RemittanceMethod): AccountKey {
  return REMITTANCE_ACCOUNTS[method];
}

export const PAYOUT_METHODS = ['cash', 'mpesa', 'bank_transfer', 'cheque'] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

export const PAYOUT_METHOD_WORDS: Record<PayoutMethod, string> = {
  cash: 'Cash',
  mpesa: 'M-Pesa',
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
};

const PAYOUT_ACCOUNTS: Record<PayoutMethod, AccountKey> = {
  cash: 'money.cash',
  mpesa: 'money.mpesa',
  bank_transfer: 'money.bank',
  cheque: 'money.bank',
};

export function payoutAccountOf(method: PayoutMethod): AccountKey {
  return PAYOUT_ACCOUNTS[method];
}

/** One override of the default chart. `scopeId` is a payer for payer-scoped keys. */
export interface AccountMapping {
  readonly key: AccountKey;
  readonly scopeId: string | null;
  readonly code: string | null;
  readonly name: string;
}

export interface ResolvedAccount {
  readonly code: string | null;
  readonly name: string;
  /** Where the answer came from: this payer's mapping, the key's mapping, or the default. */
  readonly source: 'scoped' | 'mapped' | 'default';
}

/**
 * The account a line posts to: the payer's own mapping first (for payer-scoped keys), then
 * the key's mapping, then the default name with no code.
 */
export function resolveAccount(
  key: AccountKey,
  scopeId: string | null,
  mappings: readonly AccountMapping[],
): ResolvedAccount {
  if (scopeId !== null) {
    const scoped = mappings.find((m) => m.key === key && m.scopeId === scopeId);
    if (scoped !== undefined) return { code: scoped.code, name: scoped.name, source: 'scoped' };
  }
  const mapped = mappings.find((m) => m.key === key && m.scopeId === null);
  if (mapped !== undefined) return { code: mapped.code, name: mapped.name, source: 'mapped' };
  return { code: null, name: accountDefault(key).name, source: 'default' };
}
